<script lang="ts" setup>
/*
  Todo:
  - why we need the filters.lenght <ProductListSearchProvider v-if="filters.length > 0" :sorting="sortingRef"> here? It breaks if we remove that condition with the isIOS() but this way in every place we use this we need to make sure pass something as filter, like "stock > 0", eg the search.vue
*/
import { AisPagination } from "vue-instantsearch/vue3/es";
import { viewItemList as sendViewItemListEvent } from "~/composables/useAnalytics";
import type { MasterProduct } from "~/types/common";
import { useFilterRegistry } from "~/stores/filterRegistry";
// const sortingOptions = [];

// -----------------------
// composables
// -----------------------
const config = useRuntimeConfig();
const route = useRoute();
const { isRealContent, isIOS, getUrlHash } = useHelpers();
const { status: authStatus, data: authData } = useAuth();
const { t } = useI18n();
const filterRegistry = useFilterRegistry();

// Include Satellite theme.
// Fixme: we only use it now for hierarchical filter, should remove after making our own version of that.
useHead({
  style: [
    {
      href: "https://cdn.jsdelivr.net/npm/instantsearch.css@8.0.0/themes/satellite-min.css",
      integrity: "sha256-p/rGN4RGy6EDumyxF9t7LKxWGg6/MZfGhJM/asKkqvA=",
      crossorigin: "anonymous",
    },
  ],
});

// -----------------------
// reactive properties
// -----------------------
const themeGroup = config.public.theme.group;
const hasResults = ref(false);
const searchResults = ref();
const searchComplete = ref(false);

const props = defineProps<{
  filters?: string;
  showOutOfStock?: boolean;
  query?: any;
  hideFacets?: Array<string> | null;
  title: string;
  beforeContent?: string | null;
  afterContent?: string | null;
  // fixme: these are not actually used yet
  class?: string | null;
  style?: string | null;
  preserveScrollPosition?: boolean | null;
}>();

const showFiltersOnMobile = ref(false);
const hitsNumber = ref(0);
const sortingRef = ref(route.query?.sorting ?? "sku:desc");
const filterContainerClasses = computed(() => {
  if (
    props.beforeContent &&
    props.afterContent &&
    props.beforeContent.length > 0 &&
    props.afterContent.length > 0
  ) {
    return "row-span-3";
  } else if (
    (props.beforeContent && props.beforeContent.length > 0) ||
    (props.afterContent && props.afterContent.length > 0)
  ) {
    return "row-span-2";
  }

  return "";
});

const extendedFilters = computed(() => {
  const parts = [];

  if (!props.showOutOfStock) {
    parts.push("stock > 0");
  }

  if (props.filters && props.filters.length > 0) {
    parts.push(props.filters);
  }

  return parts.join(" AND ");
});

const changeSorting = (newSorting: string) => {
  sortingRef.value = newSorting;
};

const handleSearchResultsUpdate = (products: MasterProduct[]) => {
  sendViewItemListEvent(products, { listName: props.title });
};

const handleSearchComplete = (hits: boolean) => {
  // console.log("search is complete, has hits: " + hits);
  searchComplete.value = true;
  if (hits) {
    hasResults.value = true;    
  }
};

const setHitsNumber = (hitsNr: number) => {
  hitsNumber.value = hitsNr;
};

const priceFieldToUse = computed(() => {
  if (authStatus.value === "authenticated") {
    if (
      Array.isArray(authData?.value?.usergroups) &&
      authData?.value?.usergroups?.includes(1)
    ) {
      return "h";
    }
  }

  return "c";
});

const getFilterWiseUrlHash = () => {
  // White list query parameters to avoid eg. utm params to change the final hash
  const validParams = filterRegistry.productListFilters.map(
    (filter) => filter.attribute,
  );

  validParams.push("page");
  validParams.push("sorting");
  validParams.push("query"); // Search page

  return getUrlHash(validParams);
};

const updateScrollY = () => {
  const productCards = document.body.querySelectorAll(
    ".results [id^=product-]",
  );

  for (const productCard of productCards) {
    const boundingClientRect = productCard.getBoundingClientRect();

    if (boundingClientRect.top > 56 /* $menu-height */) {
      setScrollDetails(productCard.id, boundingClientRect.top);
      break;
    }
  }
};

const getScrollPositions = (): Record<string, any> => {
  const raw = sessionStorage.getItem("scrollPositions");

  return raw ? JSON.parse(raw) : {};
};

const setScrollDetails = (element: string, margin: number) => {
  const scrollPositions = getScrollPositions();
  const urlHash = getFilterWiseUrlHash();

  if (!scrollPositions[urlHash]) {
    scrollPositions[urlHash] = {};
  }

  scrollPositions[urlHash].element = element;
  scrollPositions[urlHash].margin = margin;
  scrollPositions[urlHash].scrollTop =
    window.scrollY ||
    document.body.scrollTop ||
    document.documentElement.scrollTop;

  if (scrollPositions[urlHash].scrollTop <= 0) {
    // Scrolled back to top, unnecessary to store it
    delete scrollPositions[urlHash];
  }

  sessionStorage.setItem("scrollPositions", JSON.stringify(scrollPositions));
};

const stopSavingScrollPosition = () => {
  window.removeEventListener("scroll", updateScrollY);
};

const restoreScrollPosition = () => {
  if (!props.preserveScrollPosition) {
    return;
  }

  const urlHash = getFilterWiseUrlHash();
  const scrollPositions = getScrollPositions();
  const scrollPosition =
    urlHash in scrollPositions ? scrollPositions[urlHash] : {};

  if (scrollPosition.scrollTop) {
    const restoreScrollStarted = Date.now();
    const restoreScrollFn = setInterval(() => {
      const productCardsContainer =
        searchResults.value?.$el ?? document.body.querySelector(".results");

      if (productCardsContainer instanceof HTMLElement) {
        setTimeout(() => {
          window.scrollTo({
            top: scrollPosition.scrollTop,
            behavior: "instant",
          });
          window.addEventListener("scroll", updateScrollY);
        }, 500);

        clearInterval(restoreScrollFn);
      }

      if (Date.now() - restoreScrollStarted > 10000) {
        window.addEventListener("scroll", updateScrollY);
        clearInterval(restoreScrollFn);
      }
    }, 50);
  } else {
    window.addEventListener("scroll", updateScrollY);
  }
};

if (import.meta.client && props.preserveScrollPosition !== false) {
  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }

  onMounted(restoreScrollPosition);
  onUnmounted(() => {
    window.removeEventListener("scroll", updateScrollY);
  });

  // Handling back navigation
  window.addEventListener("pageshow", restoreScrollPosition);
}
</script>

<template>
  <div v-if="!searchComplete">
    <!-- Skeleton loader shown before results are ready -->
    <div
      class="h-[80vh] bg-brand-secondary animate-pulse rounded m-5 opacity-25"
    ></div>
  </div>
  <div>
      <div v-show="searchComplete && hasResults">
      <div class="flex flex-row items-center mb-3 px-20">
        <h1
          v-if="title && title.length > 0"
          class="!text-3xl font-bold leading-9 font-lora"
          style="margin-top: 0 !important"
        >
          {{ title }}
        </h1>
      </div>

      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="isRealContent(beforeContent)"
        class="mb-5 hidden md:block px-20"
        v-html="beforeContent"
      ></div>
      <!--eslint-enable-->

      <ProductListSearchProvider
        v-if="filters.length > 0"
        :sorting="sortingRef"
        @state-change-start="stopSavingScrollPosition"
        @state-change-end="restoreScrollPosition"
      >
        <ProductListConfigure :filters="extendedFilters" :query="query" />
        <!-- Early-initialization needed in order to restore pagination state -->
        <AisPagination class="hidden"></AisPagination>
        <div v-show="hasResults" id="productListContainer" class="px-20">
          <div class="flex flex-row items-center mb-3">
            <span class="mt-[7px] ml-3 text-nowrap"
              ><ProductListSearchStats @hits-number-update="setHitsNumber"
            /></span>
          </div>

          <BaseCollapsibleBlock
            v-if="isRealContent(beforeContent)"
            :content="beforeContent"
            class="md:hidden mb-[2px]"
          ></BaseCollapsibleBlock>

          <div class="flex mb-5">
            <ProductListSearchBar class="hidden" />
            <ProductListCurrentRefinements :hits-number="hitsNumber" />
          </div>

          <div class="mb-5 p-20 rounded-16 bg-brand-surface">
            <div class="grid grid-cols-12 gap-20">
              <div
                class="col-span-12 md:col-span-4 xl:col-span-2"
                :class="filterContainerClasses"
              >
                <div
                  id="productListFilters"
                  class="filters md:block max-md:p-[25px] max-md:fixed max-md:overflow-y-scroll max-md:left-0 max-md:bg-brand-surface max-md:w-full mt-0 z-productlistfilter"
                  :class="{ hidden: !showFiltersOnMobile, ios: isIOS() }"
                >
                  <div class="flex justify-between md:hidden mb-[2px]">
                    <span class="">
                      <i class="m-icon-filter text-[15px] mr-[5px]"></i>
                      {{ t("filter") }}
                    </span>

                    <span
                      class="text-brand-headline text-sm underline"
                      @click="showFiltersOnMobile = false"
                      >{{ t("close") }}</span
                    >
                  </div>
                  <ProductListCurrentRefinements
                    class="max-md:mb-[2px] md:hidden"
                    :hits-number="hitsNumber"
                  />
                  <ProductListSortingPanel
                    class="mb-5"
                    :is-open="true"
                    :sorting="sortingRef"
                    @update:value="changeSorting"
                  ></ProductListSortingPanel>
                  <ProductListHierarchicalFilter
                    :attribute-name="t('product_list.filters.kategoria')"
                    class="mb-5"
                    attribute="v"
                    :is-open="true"
                  />
                  <ProductListFacetFilter
                    v-if="!hideFacets?.includes('masterproduct_nem_property')"
                    attribute="b"
                    :attribute-name="t('product_list.filters.nem')"
                    sort-by="spec_alphabet"
                    class="mb-5"
                    :is-open="true"
                  />
                  <ProductListPriceFilter
                    v-if="!hideFacets?.includes('masterproduct_price')"
                    :attribute="priceFieldToUse"
                    :attribute-name="t('product_list.filters.ar')"
                    class="mb-5"
                    :is-open="true"
                    :hide-in-action-filter="filters?.includes('(c.')"
                    :step="1"
                  />
                  <ProductListFacetFilter
                    v-if="!hideFacets?.includes('product_meret_property')"
                    attribute="e"
                    :attribute-name="t('product_list.filters.meret')"
                    sort-by="spec_meret"
                    class="mb-5"
                    :is-open="true"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes('masterproduct_gyarto_property') &&
                      themeGroup !== 'tnf'
                    "
                    attribute="a"
                    :attribute-name="t('product_list.filters.gyarto')"
                    class="mb-5"
                    :is-open="true"
                  />
                  <ProductListPaletteFilter
                    v-if="!hideFacets?.includes('product_paletta_property')"
                    attribute="d"
                    :attribute-name="t('color')"
                    class="mb-5"
                    :is-open="true"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_felhasz_property',
                      )
                    "
                    attribute="r_a"
                    :attribute-name="t('product_list.filters.felhasznalas')"
                    class="mb-5"
                    :is-open="true"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_tulajd_property',
                      )
                    "
                    attribute="s_a"
                    :attribute-name="t('product_list.filters.tulajdonsag')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_anyag_property',
                      )
                    "
                    attribute="f"
                    :attribute-name="t('product_list.filters.anyag')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_belsoa_property',
                      )
                    "
                    attribute="aa"
                    :attribute-name="t('product_list.filters.belsoa')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_felso_property',
                      )
                    "
                    attribute="z"
                    :attribute-name="t('product_list.filters.felso')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListRangeFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_tomegl_property',
                      )
                    "
                    attribute="n_n"
                    :attribute-name="t('product_list.filters.tomeg')"
                    class="mb-5"
                    data-type="weight"
                    :step="1"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_toltoany_property',
                      )
                    "
                    attribute="j"
                    :attribute-name="t('product_list.filters.toltoanyag')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_toltoanyl_property',
                      )
                    "
                    attribute="ab"
                    :attribute-name="t('product_list.filters.toltoanyagleiras')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_ujhossz_property',
                      )
                    "
                    attribute="k"
                    :attribute-name="t('product_list.filters.ujjhossz')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_talp_property',
                      )
                    "
                    attribute="l"
                    :attribute-name="t('product_list.filters.talp')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListRangeFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_urtartalom_property',
                      )
                    "
                    attribute="m_n"
                    :attribute-name="t('product_list.filters.urtartalom')"
                    class="mb-5"
                    data-type="capacity"
                    :step="1"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_ferohelyl_property',
                      )
                    "
                    attribute="o"
                    :attribute-name="t('product_list.filters.ferohelyl')"
                    sort-by="spec_alphabet"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_ferohely_property',
                      )
                    "
                    attribute="q"
                    :attribute-name="t('product_list.filters.ferohely')"
                    sort-by="spec_alphabet"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListRangeFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_konflimit_property',
                      )
                    "
                    attribute="p_n"
                    :attribute-name="t('product_list.filters.konflimit')"
                    class="mb-5"
                    data-type="celsius"
                    :step="1"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_bejarat_property',
                      )
                    "
                    attribute="t"
                    :attribute-name="t('product_list.filters.bejarat')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_rudazat_property',
                      )
                    "
                    attribute="i"
                    :attribute-name="t('product_list.filters.rudazat')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_atmero_property',
                      )
                    "
                    attribute="w"
                    :attribute-name="t('product_list.filters.atmero')"
                    class="mb-5"
                    sort-by="spec_atmero"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_testmagas_property',
                      )
                    "
                    attribute="x"
                    :attribute-name="t('product_list.filters.testmagas')"
                    sort-by="spec_alphabet"
                    class="mb-5"
                    :is-open="false"
                  />
                  <ProductListFacetFilter
                    v-if="
                      !hideFacets?.includes(
                        'masterproduct_termekjellemzo_oli_karabjel_property',
                      )
                    "
                    attribute="y_a"
                    :attribute-name="t('product_list.filters.karabjel')"
                    class="mb-5"
                    :is-open="false"
                  />
                  <!--
                  <ProductListFacetFilter
                    v-if="!hideFacets?.includes('available_in_stores')"
                    attribute="u"
                    :attribute-name="t('product_list.filters.stock')"
                    sort-by="spec_alphabet"
                    class="mb-5"
                    :is-open="false"
                  />
                  -->
                </div>
              </div>
              <div class="col-span-12 md:col-span-8 xl:col-span-10">
                <div class="results">
                  <div class="results-meta">
                    <!-- <ProductListSearchStats /> -->
                    <!-- <MeiliSearchSorting /> -->
                  </div>
                  <ProductListSearchResults
                    ref="searchResults"
                    @update="handleSearchResultsUpdate"
                    @search-complete="handleSearchComplete"
                    @has-items="hasResults = true"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <ProductListFilterButton
          :filters-visible="showFiltersOnMobile"
          result-type="product_list.hits"
          @toggle="showFiltersOnMobile = !showFiltersOnMobile"
        ></ProductListFilterButton>
      </ProductListSearchProvider>

      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="isRealContent(afterContent)"
        class="max-w-[1000px] mx-auto px-20"
        v-html="afterContent"
      ></div>
      <!--eslint-enable-->
    </div>
    <div v-if="searchComplete && !hasResults">
      <div class="flex flex-row items-center mb-3 px-20">
        <h1
          class="!text-3xl font-bold leading-9 font-lora"
          style="margin-top: 0 !important"
        >
          {{ t("product_list.no_hits") }}
        </h1>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$menu-height: 56px;
$filter-button-height: 44px;
$filter-button-bottom: 50px;

@media (max-width: 767px) {
  #productListFilters {
    top: $menu-height;
    height: calc(100vh - $menu-height);
    padding-bottom: calc($filter-button-height + $filter-button-bottom);

    &.ios {
      padding-bottom: calc(
        $filter-button-height + $filter-button-bottom + 80px
      );
    }
  }
}
</style>
